// Per-slot "generation" counters that let an in-flight image compression
// know, once it resolves, whether its result still belongs to the pick that
// started it. FeedbackCompose has three fixed image slots; compressing a
// picked file is asynchronous (compressImage awaits createImageBitmap and
// canvas.toBlob), and nothing stops the player from clearing that slot — or
// picking a different file for it — before the promise settles. Without
// this check, a stale compression result gets applied after the fact and
// resurrects an image the player already removed, with a fresh object URL
// they never asked for.
//
// Pulled out as pure array operations (no refs, no React, no async) so the
// guard itself — "does this result still belong to the pick that started
// it" — is unit-testable without simulating a real async race.

/**
 * Call once whenever a slot's identity changes: a file was just picked for
 * it (compression is about to start), or the slot was just cleared. Both are
 * "whatever was previously in flight for this slot no longer counts" events.
 * Returns the updated generations array plus the generation number just
 * claimed — only meaningful for a pick-start call; a removal only needs the
 * updated array.
 */
export function bumpSlotGeneration(generations: number[], index: number): { generations: number[]; generation: number } {
  const next = generations.slice();
  next[index] = (next[index] ?? 0) + 1;
  return { generations: next, generation: next[index] };
}

/**
 * Call right before applying an async compression result to a slot. False
 * means the slot was removed, or re-picked, after this attempt started — the
 * result must be discarded (and any object URL it already created revoked),
 * never written into state.
 */
export function isCurrentSlotGeneration(generations: number[], index: number, generation: number): boolean {
  return generations[index] === generation;
}
